import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

export type LocationPermissionReason = "denied" | "whenInUse" | "notGranted";

interface LocationPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason: LocationPermissionReason;
  onOpenSettings: () => void | Promise<void>;
}

const REASON_CONFIG: Record<
  LocationPermissionReason,
  { title: string; description: string }
> = {
  denied: {
    title: "Location permission required",
    description:
      "Straiv needs access to your location to track your runs. You've denied access or it was turned off.\n\n" +
      "To enable:\n" +
      "1. Tap Open Settings below\n" +
      "2. Go to Straiv → Location\n" +
      "3. Select \"Always\" (required for tracking when your phone is locked)",
  },
  whenInUse: {
    title: "Location must be set to Always",
    description:
      "Straiv needs \"Always\" location access to track your runs when your phone is locked.\n\n" +
      "You currently have \"While Using the App\" only. To fix:\n" +
      "1. Tap Open Settings below\n" +
      "2. Go to Straiv → Location\n" +
      "3. Select \"Always\"",
  },
  notGranted: {
    title: "Location permission required",
    description:
      "Straiv needs access to your location to track your runs.\n\n" +
      "To enable:\n" +
      "1. Tap Open Settings below\n" +
      "2. Go to Straiv → Location\n" +
      "3. Select \"Always\" (required for tracking when your phone is locked)",
  },
};

export const LocationPermissionDialog = ({
  open,
  onOpenChange,
  reason,
  onOpenSettings,
}: LocationPermissionDialogProps) => {
  const { t } = useTranslation();
  const config = REASON_CONFIG[reason];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="location-dialog-description">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle>{config.title}</DialogTitle>
          </div>
          <DialogDescription
            id="location-dialog-description"
            className="whitespace-pre-line text-left"
          >
            {config.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={async () => {
              await onOpenSettings();
              // Keep dialog open - will auto-close when user returns and permission is granted
            }}
            className="w-full"
          >
            <Settings className="mr-2 h-4 w-4" />
            {t('common.openSettings')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
